import { get, isEmpty, map } from 'lodash'
import moment from 'moment'
import React, { ReactElement } from 'react'

import { getNextRun, getStringFromCron } from '../../CronConfigurator/utils'
import Icon from '../../Icon'
import { TColumn, TTabelCellProps } from '../../InfinityTable/types'
import TransitionMantineTooltip from '../../TransitionMantineTooltip'

export const defaultColumns = getDefaultColumns()

export function getDefaultColumns(): TColumn[] {
  return [
    {
      label: '#',
      dataKey: 'id',
      sortKey: 'id',
      flexGrow: 0,
      cellRenderer: (cellProps: TTabelCellProps): ReactElement => {
        const { rowData } = cellProps
        return (
          <>
            <div className="text-center">
              <div>{rowData.id}</div>
              <div>
                {rowData.status === 'failed' && (
                  <Icon
                    icon="fas fa-exclamation-triangle"
                    danger
                    tooltip={i18n.t(
                      'cron_order_error_tooltip',
                      'Dieser Dauerauftrag ist fehlerhaft. Bitte prüfen Sie die Einstellungen.',
                    )}
                  />
                )}
              </div>
            </div>
          </>
        )
      },
    },
    {
      label: i18n.t('name', 'Name'),
      dataKey: 'name',
      sortKey: 'name',
      flexGrow: 1,
    },
    {
      label: i18n.t('execution_pattern', 'Ausführungsrhythmus'),
      dataKey: ['cronPattern', 'cronConfig'],
      cellRenderer: (props: TTabelCellProps): string | null => {
        const { cellData } = props

        if (!cellData) return null
        return getStringFromCron(cellData.cronPattern, cellData.cronConfig)
      },
      flexGrow: 1,
    },
    {
      label: i18n.t('next_5_execution', 'Nächste 5 Ausführungen'),
      dataKey: ['cronPattern', 'cronConfig'],
      cellRenderer: (props: TTabelCellProps): React.ReactNode => {
        const { cellData } = props

        if (get(cellData, 'cronConfig.cronType') === 'once') {
          return ''
        }

        const nextRuns = getNextRun(
          get(cellData, 'cronPattern', ''),
          moment().toISOString(),
          5,
          get(cellData, 'cronConfig.cronTimes', []),
        )
        return map(nextRuns || [], (run) => <div>{run}</div>)
      },
    },
    {
      label: i18n.t('colis_colis', 'Colis'),
      dataKey: 'order.colis',
      colWidth: 60,
      cellRenderer: (props: TTabelCellProps): React.ReactElement => {
        return (
          <div {...props}>
            {Array.isArray(props.cellData) ? (
              <span>
                {props.cellData.length} <i className="fas fa-cube" />
              </span>
            ) : null}
          </div>
        )
      },
    },
    {
      label: i18n.t(`sla_capcase`, `SLA`),
      dataKey: 'order',
      colWidth: 20,
      cellRenderer: (props: TTabelCellProps): React.ReactElement => {
        const { cellData } = props

        return (
          <div {...props}>
            <span>{get(cellData, 'sla')}</span>{' '}
            {!isEmpty(cellData.additional_services) && (
              <TransitionMantineTooltip
                tooltip={
                  <div>
                    <React.Fragment>
                      <h6 style={{ color: 'white' }}>
                        {i18n.t('additional_services', 'Zusatzservices')}:
                      </h6>
                      {map(cellData.additional_services, (service) => (
                        <React.Fragment>
                          {' '}
                          <span>
                            {service.description}{' '}
                            {service.name === 'COD' &&
                              `${get(cellData, 'cashondeliveryAmount')} ${get(
                                cellData,
                                'cashondeliveryCurrency',
                              )}`}
                          </span>
                          <br />{' '}
                        </React.Fragment>
                      ))}
                    </React.Fragment>
                  </div>
                }
              >
                <i className="fas fa-question-circle" />
              </TransitionMantineTooltip>
            )}
          </div>
        )
      },
    },

    {
      label: i18n.t('pickUpAddress', 'Abholadresse'),
      dataKey: 'order',
      cellRenderer: (props: TTabelCellProps): React.ReactElement => {
        const { cellData } = props

        return (
          <div
            {...props}
            className="py-1"
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            <span>
              {cellData.senderName1} {cellData.senderName1 && <br />}
              {cellData.senderName2} {cellData.senderName2 && <br />}
              {cellData.senderName3} {cellData.senderName3 && <br />}
              {cellData.senderStreet} {cellData.senderStreetNumber}{' '}
              {cellData.senderStreetNumberAddition}
              <br />
              {cellData.senderZip} {cellData.senderCity}{' '}
              {cellData.senderCountry}
            </span>
          </div>
        )
      },
    },
    {
      label: i18n.t('deliveryAddress', 'Zustelladresse'),
      dataKey: 'order',
      cellRenderer: (props: TTabelCellProps): React.ReactElement => {
        const { cellData } = props

        return (
          <div
            {...props}
            className="py-1"
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            <span>
              {cellData.receiverName1} {cellData.receiverName1 && <br />}
              {cellData.receiverName2} {cellData.receiverName2 && <br />}
              {cellData.receiverName3} {cellData.receiverName3 && <br />}
              {cellData.receiverStreet} {cellData.receiverStreetNumber}{' '}
              {cellData.receiverStreetNumberAddition}
              <br />
              {cellData.receiverZip} {cellData.receiverCity}{' '}
              {cellData.receiverCountry}
            </span>
            )
          </div>
        )
      },
    },
  ]
}
